import type { ProvenanceDocumentKind, ProvenanceMethod } from "@ondo/shared";

import { AttachOndoId } from "./AttachOndoId.tsx";
import { ProvenanceDateField } from "./ProvenanceDateField.tsx";
import {
  ACQUISITION_METHODS,
  DOCUMENT_KINDS,
  HISTORY_COPY,
  HISTORY_KINDS,
  PROVENANCE_COPY,
} from "./provenance.constants.ts";
import type { DraftEntry, OndoLine } from "./provenance.model.ts";

/**
 * The fields one kind of history is made of.
 *
 * NO `switch (kind)` HERE, and that is the point of the whole shape. This
 * component walks `HISTORY_KINDS[kind].fields` and renders whatever it finds, so
 * an exhibition and a condition report differ by a table entry rather than by a
 * branch — and a new kind is a new object in constants, not a new component
 * and not another arm of a conditional that somebody has to remember to write.
 *
 * The three `control` arms below are the exception that proves it: they are
 * per-CONTROL and not per-kind, so OND-95's `insurance` needed one new arm for
 * its money field and no knowledge of insurance anywhere in this file.
 *
 * Used by both surfaces that compose an entry: the modal on the work record and
 * step 3 of the register wizard. The wizard only ever passes `sale`, which is
 * what it recorded before any of this existed.
 */
export function HistoryFields({
  base,
  values,
  ondo,
  onSetField,
  onAttachOndoId,
  onDetachOndoId,
}: {
  /** One useId per form, so every field id in it is unique and stable. */
  base: string;
  values: DraftEntry;
  ondo: OndoLine | null;
  onSetField: <K extends keyof DraftEntry>(key: K, value: DraftEntry[K]) => void;
  onAttachOndoId: (raw: string) => string | null;
  onDetachOndoId: () => void;
}) {
  const model = HISTORY_KINDS[values.kind];

  return (
    <>
      <div className="provenance__grid">
        {model.fields.map((field) => {
          if (field.control === "method") {
            return (
              <div className="provenance__field" key="method">
                <label htmlFor={`${base}-method`}>{field.label}</label>
                <select
                  id={`${base}-method`}
                  value={values.method}
                  onChange={(event) =>
                    onSetField("method", event.target.value as ProvenanceMethod)
                  }
                >
                  {ACQUISITION_METHODS.map((method) => (
                    <option key={method.key} value={method.key}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          /**
           * The amount insured and its currency, as one field with two boxes.
           *
           * ONE ENTRY IN THE TABLE AND NOT TWO, because they are one fact and
           * the database refuses half of it. The currency sits first and narrow
           * — the same arrangement `ConsignPanel` uses — so the figure reads
           * left to right as somebody says it.
           */
          if (field.control === "money") {
            return (
              <div className="provenance__field provenance__field--money" key="money">
                <label htmlFor={`${base}-insured`}>{field.label}</label>
                <div className="provenance__money">
                  <input
                    id={`${base}-insured-currency`}
                    className="provenance__money-currency"
                    aria-label={PROVENANCE_COPY.insuredCurrencyLabel}
                    value={values.insuredCurrency}
                    maxLength={3}
                    onChange={(event) => onSetField("insuredCurrency", event.target.value)}
                  />
                  <input
                    id={`${base}-insured`}
                    className="provenance__money-amount"
                    value={values.insuredAmount}
                    inputMode="decimal"
                    placeholder="e.g. 1,850,000"
                    onChange={(event) => onSetField("insuredAmount", event.target.value)}
                  />
                </div>
                <p className="provenance__help">{field.help}</p>
              </div>
            );
          }

          if (field.control === "documentKind") {
            return (
              <div className="provenance__field" key="documentKind">
                <label htmlFor={`${base}-doc-kind`}>{field.label}</label>
                <select
                  id={`${base}-doc-kind`}
                  value={values.documentKind}
                  onChange={(event) =>
                    onSetField("documentKind", event.target.value as ProvenanceDocumentKind)
                  }
                >
                  {DOCUMENT_KINDS.map((kind) => (
                    <option key={kind.key} value={kind.key}>
                      {kind.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          return (
            <div className="provenance__field" key={field.key}>
              <label htmlFor={`${base}-${field.key}`}>{field.label}</label>
              <input
                id={`${base}-${field.key}`}
                value={values[field.key]}
                placeholder={field.placeholder}
                // `readOnly` AND NOT `disabled`, for a fixed field. A disabled
                // input is skipped by the keyboard and is announced as
                // unavailable, and this value is neither: it is the claim the
                // entry is about and the thing a reader most needs to check
                // before pressing a one-way button. Read-only is focusable,
                // selectable and copyable, and still refuses an edit.
                readOnly={field.fixed === true}
                {...(field.fixed === true ? { className: "provenance__input--fixed" } : {})}
                onChange={(event) => onSetField(field.key, event.target.value)}
              />
              {/* Why a box that cannot be typed in is not broken, said where it
                  is met. What is at stake if it COULD be edited — a studio shown
                  a claim whose words and id disagree — is in `HistoryField`. */}
              {field.fixed === true && (
                <p className="provenance__help">{HISTORY_COPY.fixedHelp}</p>
              )}
              {/* Only ever under a field that names a person or a body who could
                  hold an account. There is no lookup behind it — see
                  `AttachOndoId` — so it repeats an id the author already had. */}
              {field.ondo && (
                <AttachOndoId
                  attached={ondo}
                  onAttach={onAttachOndoId}
                  onDetach={onDetachOndoId}
                />
              )}
            </div>
          );
        })}

        <ProvenanceDateField
          id={`${base}-start`}
          label={model.dateLabels.start}
          value={values.startDate}
          onChange={(next) => onSetField("startDate", next)}
        />

        {/* A point kind renders one cascade and no second one. An auction
            happened on a date; offering it an end date invites somebody to
            record a fact that does not exist. */}
        {model.dates === "range" && model.dateLabels.end !== null && (
          <ProvenanceDateField
            id={`${base}-end`}
            label={model.dateLabels.end}
            value={values.endDate}
            {...(model.dateLabels.endHelp === null
              ? {}
              : { help: model.dateLabels.endHelp })}
            onChange={(next) => onSetField("endDate", next)}
          />
        )}
      </div>

      <div className="provenance__field">
        <label htmlFor={`${base}-notes`}>{model.notes.label}</label>
        <textarea
          id={`${base}-notes`}
          rows={3}
          value={values.notes}
          placeholder={model.notes.placeholder}
          onChange={(event) => onSetField("notes", event.target.value)}
        />
      </div>
    </>
  );
}
